// frontend/src/components/admin/StackPanel.tsx
// ───────────────────────────────────────────────────────────────────
// Surcouche « pile » : la fiche glisse depuis la droite (translateX), la
// liste derrière recule en parallaxe (CSS `body[data-stack]` dans index.css)
// et s'assombrit. Fermer = animation inverse puis `navigate(-1)` (on dépile
// l'entrée d'historique que l'ouverture a empilée).
//
// Le retour du navigateur / le geste de Safari dépilent aussi, sans l'animation
// de sortie : la surcouche est simplement démontée.
// ───────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DetailCloseContext } from './detail-stack';

import type { JSX, ReactNode } from 'react';

/** Durée de l'animation (ms) — gardée alignée avec `duration-[380ms]` ci-dessous et index.css. */
export const STACK_DURATION_MS = 380;

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function StackPanel({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const closing = useRef(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Montage : on laisse le navigateur peindre l'état initial (hors écran) avant de glisser.
  useEffect(() => {
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setOpen(true));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, []);

  // La liste derrière recule tant que la fiche est ouverte.
  useEffect(() => {
    if (!open) return;
    document.body.dataset['stack'] = 'open';
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      delete document.body.dataset['stack'];
    };
  }, [open]);

  const close = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    setOpen(false);
    window.setTimeout(
      () => {
        void navigate(-1);
      },
      prefersReducedMotion() ? 0 : STACK_DURATION_MS,
    );
  }, [navigate]);

  return (
    <DetailCloseContext.Provider value={close}>
      <div className="fixed inset-0 z-50" data-testid="stack-panel">
        <div
          aria-hidden="true"
          className={`absolute inset-0 bg-black transition-opacity duration-[380ms] ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none ${open ? 'opacity-40' : 'opacity-0'}`}
        />
        <div
          ref={panelRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label="Fiche"
          className={`absolute inset-0 overflow-y-auto overscroll-contain bg-stone-950 shadow-[-12px_0_30px_rgba(0,0,0,0.5)] transition-transform duration-[380ms] ease-[cubic-bezier(0.32,0.72,0,1)] focus:outline-none motion-reduce:transition-none ${open ? 'translate-x-0' : 'translate-x-full'}`}
        >
          {children}
        </div>
      </div>
    </DetailCloseContext.Provider>
  );
}
